import { Component, Input, OnInit } from "@angular/core";
import { Banner, ChangeBannerObject } from "../banners.interface";
import { ChangeBannerAction, RemoveBannerAction } from "../banners.actions";
import { Store } from "@ngrx/store";
import { ConfirmationDialogComponent } from "../../../shared/confirmation-dialog/confirmation-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../../../shared/toastr.service";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { StartCheckingForInstallationInProgressAction } from "../../../signature-installation-state/signature-installation-state.actions";
import { Image } from "../../../shared/image-uploader/image.interface";
import { ImageService } from "../../../shared/image-uploader/image.service";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../../shared/error-state-matcher";

@Component({
  selector: "app-banner",
  templateUrl: "./banner.component.html",
  styleUrls: ["./banner.component.scss"],
  standalone: false,
})
export class BannerComponent implements OnInit {
  @Input() banner: Banner;
  form: UntypedFormGroup;
  editMode = false;
  file;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private translate: TranslateService,
    private toastr: ToastrService,
    public imageService: ImageService,
  ) {}

  ngOnInit(): void {
    this.fillInitialForm();
  }

  fillInitialForm = (): void => {
    this.form = new UntypedFormGroup({
      addonId: new UntypedFormControl(this.banner.addonId, (c) =>
        Validators.required(c),
      ),
      file: new UntypedFormControl(""),
      name: new UntypedFormControl(this.banner.name, (c) =>
        Validators.required(c),
      ),
      url: new UntypedFormControl(this.banner.url, (c) =>
        Validators.required(c),
      ),
      utm: new UntypedFormControl(this.banner.utm, (c) =>
        Validators.required(c),
      ),
      imageWidth: new UntypedFormControl(this.banner.imageWidth, (c) =>
        Validators.min(0)(c),
      ),
      imageHeight: new UntypedFormControl(this.banner.imageHeight, (c) =>
        Validators.min(0)(c),
      ),
      originalImageWidth: new UntypedFormControl(
        this.banner.originalImageWidth,
        (c) => Validators.min(0)(c),
      ),
      originalImageHeight: new UntypedFormControl(
        this.banner.originalImageHeight,
        (c) => Validators.min(0)(c),
      ),
    });
  };

  changeBanner = (): void => {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid) {
      return;
    }
    this.store.dispatch(new StartCheckingForInstallationInProgressAction());
    const formValue = this.form.value as ChangeBannerObject;
    this.store.dispatch(new ChangeBannerAction(formValue));
  };

  cancelEditBanner = (): void => {
    this.fillInitialForm();
    this.changeEditMode();
  };

  changeEditMode = (): void => {
    this.editMode = !this.editMode;
  };

  copyBannerToClipboard = (): void => {
    void navigator.clipboard.writeText(this.banner.htmlContent);
    const message = this.translate.instant(
      "addons.banners.copyBannerSuccess",
    ) as string;
    this.toastr.success(message);
  };

  removeBanner = (addonBannerId: string): void => {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant(
            "addons.banners.deleteConfirmationText",
          ) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.store.dispatch(new RemoveBannerAction(addonBannerId));
        }
      });
  };

  fileChange = (file: Image): void => {
    this.file = file.file;
    const formValue = this.form.value as {
      originalImageWidth?: number;
      originalImageHeight?: number;
    };
    const width = file.width || formValue.originalImageWidth || 0;
    const height = file.height || formValue.originalImageHeight || 0;

    this.form.patchValue({
      file: file.file,
      originalImageWidth: String(width),
      originalImageHeight: String(height),
      imageWidth: String(width),
      imageHeight: String(height),
    });
  };

  removeFile = (): void => {
    this.file = null;
    this.form.patchValue({ file: "" });
  };
}
